<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { computed, type VNodeRef } from 'vue'
import { EbomDiffTreeDataQuery, EbomDiffTreeDataQueryVariables } from '@/generated/graphql'
import EbomDiffTreeNode from '@/components/ebom/EbomDiffTreeNode.vue'
import { sortTree } from '@/app'
import { openTreeRows, type TreeRow } from '@/utils/treeRows'
import EbomDiffSameParts from '@/components/ebom/EbomDiffSameParts.vue'

const props = defineProps<{
  oldEbomId: string
  newEbomId: string
}>()
defineEmits<{
  selectDiff: [value: EbomPartDiff]
}>()

const { t } = useI18n()

const fetchQuery = useQuery<EbomDiffTreeDataQuery, EbomDiffTreeDataQueryVariables>(
  gql`
    query EbomDiffTreeData($oldEbomId: ID!, $newEbomId: ID!) {
      article {
        oldEbom: ebom(id: $oldEbomId) {
          ...ebomPartData
        }
        newEbom: ebom(id: $newEbomId) {
          ...ebomPartData
        }
      }
    }

    fragment ebomPartData on Ebom {
      id
      name
      ebomParts {
        id
        article {
          id
        }
        articleNumber
        unit
        revision
        name
        milliQuantity
        position
        articlePath {
          articleNumbers
        }
        stepAllocations {
          id
          milliQuantity
          article {
            unit
          }
        }
      }
    }
  `,
  () => ({
    oldEbomId: props.oldEbomId,
    newEbomId: props.newEbomId,
  }),
)
const eboms = computed(() => fetchQuery.result.value?.article)
const oldEbomParts = computed(() => fetchQuery.result.value?.article.oldEbom.ebomParts || [])
const newEbomParts = computed(() => fetchQuery.result.value?.article.newEbom.ebomParts || [])

export type EbomDiffTreeEbomPart = EbomDiffTreeDataQuery['article']['oldEbom']['ebomParts'][0]
type EbomPartComparison = {
  articleNumber: string
  revision: string
  articlePath: {
    articleNumbers: string[]
  }
}
type DiffStatus = 'deleted' | 'new' | 'changed' | 'same'
export type EbomPartDiff = {
  status: DiffStatus
  articleNumber: string
  revision: string
  name?: string
  articlePath: {
    articleNumbers: string[]
  }
  inOld?: EbomDiffTreeEbomPart
  inNew?: EbomDiffTreeEbomPart
}
export type EbomPartDiffNode = {
  id: string
  diff: EbomPartDiff
  isOrContainsDiff: boolean
  children: EbomPartDiffNode[]
}

const sortEbomParts = (nodes: EbomPartDiffNode[]) =>
  sortTree(
    nodes,
    (n) => n.children.length > 0,
    (n) => {
      const position = n.diff.inNew?.position || n.diff.inOld?.position
      return position || `${n.diff.articleNumber}/${n.diff.revision}`
    },
  )
const looseKey = (p: EbomPartComparison) =>
  [p.articleNumber, ...p.articlePath.articleNumbers].join('\0')
const strictKey = (p: EbomPartComparison) => `${p.revision}\0${looseKey(p)}`
const firstByKey = (parts: EbomDiffTreeEbomPart[], key: (p: EbomPartComparison) => string) => {
  const byKey = new Map<string, EbomDiffTreeEbomPart>()
  for (const part of parts) if (!byKey.has(key(part))) byKey.set(key(part), part)
  return byKey
}

const diffs = computed<EbomPartDiff[]>(() => {
  const oldByStrictKey = firstByKey(oldEbomParts.value, strictKey)
  const newByLooseKey = firstByKey(newEbomParts.value, looseKey)
  const diffs = new Map<string, EbomPartDiff>()
  for (const part of [...oldEbomParts.value, ...newEbomParts.value]) {
    const key = looseKey(part)
    if (diffs.has(key)) continue

    const inOld = oldByStrictKey.get(strictKey(part))
    const inNew = newByLooseKey.get(key)
    let status: DiffStatus = 'same'
    if (!inNew) {
      status = 'deleted'
    }
    if (!inOld) {
      status = 'new'
    }
    if (
      !!inNew &&
      !!inOld &&
      (inNew.milliQuantity != inOld.milliQuantity || inNew.revision != inOld.revision)
    ) {
      status = 'changed'
    }

    diffs.set(key, {
      status,
      articleNumber: part.articleNumber,
      revision: part.revision,
      name: part.name,
      articlePath: part.articlePath,
      inOld,
      inNew,
    })
  }
  return [...diffs.values()]
})

const diffTreeRootNodes = computed<EbomPartDiffNode[]>(() => {
  const diffsByParentPath = new Map<string, EbomPartDiff[]>()
  for (const diff of diffs.value) {
    const parentPath = diff.articlePath.articleNumbers.slice(0, -1).join('\0')
    const siblings = diffsByParentPath.get(parentPath)
    if (siblings) siblings.push(diff)
    else diffsByParentPath.set(parentPath, [diff])
  }
  const nodes = diffs.value
    .filter((d) => d.articlePath.articleNumbers.length == 1)
    .map((d) => createNode(diffsByParentPath, d))
  return sortEbomParts(nodes)
})
function createNode(
  diffsByParentPath: Map<string, EbomPartDiff[]>,
  diff: EbomPartDiff,
): EbomPartDiffNode {
  const children = (diffsByParentPath.get(diff.articlePath.articleNumbers.join('\0')) ?? []).map(
    (d) => createNode(diffsByParentPath, d),
  )
  const isOrContainsDiff = children.some((c) => c.isOrContainsDiff) || diff.status != 'same'

  return {
    id: looseKey(diff),
    diff,
    isOrContainsDiff,
    children: sortEbomParts(children),
  }
}
// Parts without a difference anywhere below them are left out.
const rows = computed(() =>
  openTreeRows(diffTreeRootNodes.value, () => true).filter((r) => r.node.isOrContainsDiff),
)
const noDifferences = computed(() => !diffTreeRootNodes.value.some((n) => n.isOrContainsDiff))
</script>

<template>
  <div>
    <ebom-diff-same-parts
      v-if="eboms"
      :old-ebom-name="eboms?.oldEbom.name"
      :new-ebom-name="eboms?.newEbom.name"
      :diffs="diffs"
    />
    <v-virtual-scroll
      :items="rows"
      :item-key="(row) => row.node.id"
      :item-height="48"
      :renderless="true"
    >
      <template
        #default="{ item: row, itemRef }: { item: TreeRow<EbomPartDiffNode>; itemRef?: VNodeRef }"
      >
        <ebom-diff-tree-node
          :ref="itemRef"
          :node="row.node"
          :depth="row.depth"
          @select-diff="(v) => $emit('selectDiff', v)"
        />
      </template>
    </v-virtual-scroll>
  </div>
  <v-alert v-if="noDifferences && !fetchQuery.loading.value" type="info">
    {{ t('component.ebomDiffTree.noDifferences') }}
  </v-alert>
</template>

<style scoped lang="scss"></style>
